import * as React from 'react';
import { Flag, Paperclip } from 'lucide-react';
import { cn } from '../../../ui';
import { DomainBadge } from '../shared/DomainBadge';
import { UrgencyIndicator } from '../shared/UrgencyIndicator';
import { TimeAgo } from '../shared/TimeAgo';
import { useInbox } from '../InboxContext';

function EmailListItem({ email }) {
  const { selectedEmailId, selectEmail, selectedIds, toggleSelected, actions } = useInbox();
  const isSelected = selectedEmailId === email.id;
  const isChecked = selectedIds.has(email.id);

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => selectEmail(email.id)}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); selectEmail(email.id); } }}
      className={cn(
        'flex gap-2 px-2.5 py-2 cursor-pointer transition-all border-b border-nn-border/50 last:border-0',
        isSelected
          ? 'bg-aqua/[0.08] border-l-2 border-l-aqua'
          : 'hover:bg-surface-warm border-l-2 border-l-transparent',
        !email.isRead && !isSelected && 'bg-nn-white'
      )}
    >
      {/* Checkbox + star column */}
      <div className="flex flex-col items-center gap-0.5 shrink-0 pt-0.5">
        <input
          type="checkbox"
          checked={isChecked}
          onChange={(e) => { e.stopPropagation(); toggleSelected(email.id); }}
          onClick={(e) => e.stopPropagation()}
          className="h-3 w-3 rounded border-nn-border accent-aqua cursor-pointer"
        />
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); actions.toggleStar(email.id); }}
          className="flex items-center transition-colors"
          style={{ color: email.isStarred ? 'var(--red-critical)' : 'var(--surface)' }}
        >
          <Flag size={10} fill={email.isStarred ? 'var(--red-critical)' : 'none'} />
        </button>
      </div>

      {/* Unread indicator */}
      {!email.isRead && (
        <div className="shrink-0 pt-1.5">
          <span
            className="block w-1.5 h-1.5 rounded-full"
            style={{ backgroundColor: 'var(--aqua)' }}
          />
        </div>
      )}

      {/* Content */}
      <div className="flex-1 min-w-0 flex flex-col gap-px">
        {/* Row 1: sender + domain + time */}
        <div className="flex items-center gap-1.5 min-w-0">
          <span className={cn(
            'min-w-0 truncate text-[12px] leading-tight',
            !email.isRead ? 'font-semibold text-text-dark' : 'text-text-dark-secondary'
          )}>
            {email.from.name}
          </span>
          <DomainBadge domain={email.primaryDomain} />
          <UrgencyIndicator urgency={email.urgency} />
          <span className="flex-1" />
          <TimeAgo date={email.receivedAt} className="text-[10px]" />
        </div>

        {/* Row 2: subject */}
        <span className={cn(
          'truncate text-[12px] leading-tight',
          !email.isRead ? 'font-medium text-text-dark' : 'text-text-dark-secondary'
        )}>
          {email.subject}
        </span>

        {/* Row 3: preview + meta */}
        <div className="flex items-center gap-1.5 min-w-0">
          <p className="flex-1 min-w-0 truncate text-[11px] text-text-muted leading-tight">
            {email.preview}
          </p>
          {email.attachments?.length > 0 && (
            <Paperclip size={9} className="text-text-muted shrink-0" />
          )}
          {email.threadMessageCount > 1 && (
            <span className="text-[9px] text-text-muted shrink-0 tabular-nums">
              {email.threadMessageCount}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

export { EmailListItem };
